import { Injectable, signal } from '@angular/core';
import {
  UserRole,
  ROLE_CONFIGS,
  RoleConfig,
  RolePermissions,
  ROUTES_ALLOWING_SUBPATHS,
} from '../shared/types/role.interface';
import { LoggerService } from './logger.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { getActiveUserDataRaw } from '../shared/utils/impersonation-handoff.util';

/**
 * RoleService provides centralized user role management, permission checks, and route authorization helpers for the application. It exposes reactive observables for role and permissions and handles initialization, updates, and clearing of role state.
 * @component
 * @selector app-role-service
 * @example
 *   <app-role-service></app-role-service>
 * @input none - This injectable service has no Angular @Input properties. Consumers should inject the service and subscribe to the exposed observables currentUserRole$ and userPermissions$.
 * @output none - This service does not emit Angular @Output events. It exposes observables (currentUserRole$, userPermissions$) for reactive communication.
 *
 * Purpose:
 *   - Maintain the current user's role and permissions using Angular signals.
 *   - Provide observable streams (currentUserRole$, userPermissions$) for components to react to role/permission changes.
 *   - Provide utility methods for permission checks, route authorization, and role-based defaults/config lookups.
 *
 * Key properties:
 *   - currentUserRole$: Observable<UserRole | null> - Observable view of the current user role signal.
 *   - userPermissions$: Observable<RolePermissions | null> - Observable view of the current user permissions signal.
 *
 * Important methods:
 *   - initializeUserRole(userData?: any): void
 *       Initialize internal signals from provided user data or clear them when missing. Used to bootstrap role state when user information is available.
 *   - loadUserRole(userData: any): void
 *       Extracts role from user data (or falls back to a default) and calls setUserRole.
 *   - setUserRole(role: UserRole): void
 *       Set the current role signal, update permissions from ROLE_CONFIGS, and log the change.
 *   - getCurrentUserRole(): UserRole | null
 *       Return the current role synchronously from the signal.
 *   - getUserPermissions(): RolePermissions | null
 *       Return current permissions synchronously from the signal.
 *   - getRoleConfig(role: UserRole): RoleConfig
 *       Return the static role configuration for a given role.
 *   - getDefaultRoute(role: UserRole): string
 *       Return the configured default route for a given role.
 *   - hasRoutePermission(route: string, role: UserRole): boolean
 *       Determine whether a given route is allowed for the specified role. Handles exact routes, dynamic sub-paths, and special admin behavior.
 *   - hasPermission(permission: keyof RolePermissions): boolean
 *       Check whether the currently loaded permissions include a specific permission flag.
 *   - isAdmin(), isStaff(), isSuperAdmin(): boolean
 *       Convenience checks for the current role.
 *   - resolveIsAdmin(): boolean
 *       Robust admin-check that falls back to reading userData from localStorage when the role signal is uninitialized (useful during early startup or initialization races).
 *   - getDisplayRole(): string
 *       Return a human-friendly display name for the current role or a generic 'User' fallback.
 *   - clearUserRole(): void
 *       Clear role and permissions (typically used on logout) and log the action.
 *
 * Lifecycle / initialization notes:
 *   - Provided in root (singleton) via @Injectable({ providedIn: 'root' }).
 *   - The constructor accepts LoggerService and intentionally does not initialize role data there to avoid circular dependency issues; initialization is done when user data becomes available via initializeUserRole/loadUserRole.
 *   - Defaulting behavior: if user data does not contain a valid role, the service logs a warning and defaults to ADMIN for backward compatibility.
 *
 * Usage notes:
 *   - Inject the service into components or other services and subscribe to currentUserRole$ and userPermissions$ to react to changes.
 *   - Use hasRoutePermission and hasPermission for guard logic or UI enable/disable decisions.
 */
@Injectable({
  providedIn: 'root',
})
export class RoleService {
  private currentUserRole = signal<UserRole | null>(null);
  private userPermissions = signal<RolePermissions | null>(null);

  // Expose signals as observables for components
  currentUserRole$ = toObservable(this.currentUserRole);
  userPermissions$ = toObservable(this.userPermissions);

  constructor(private logger: LoggerService) {
    // Don't initialize here to avoid circular dependency
    // Initialize when needed
  }

  private initializeUserRole(userData?: any): void {
    // Check current user and set role
    if (userData) {
      this.loadUserRole(userData);
    } else {
      this.currentUserRole.set(null);
      this.userPermissions.set(null);
    }
  }

  /**
   * Load and set the user's role from provided user data, defaulting to ADMIN when role is missing or invalid.
   * @example
   * loadUserRole({ role: UserRole.ADMIN })
   * undefined
   * @param {any} userData - User information object that may contain a `role` property.
   * @returns {void} Does not return a value; assigns the resolved role via setUserRole.
   **/
  private loadUserRole(userData: any): void {
    // This would typically come from the user profile or API
    // For now, we'll determine role based on user data or API response
    if (userData) {
      // Check if role is stored in user data
      const role = (userData as any).role as UserRole;

      if (role && Object.values(UserRole).includes(role)) {
        this.setUserRole(role);
      } else {
        // Default to admin if no role is specified (for backward compatibility)
        this.logger.warn(
          'No valid role found in user data, defaulting to admin',
          { userData },
          'RoleService'
        );
        this.setUserRole(UserRole.ADMIN);
      }
    }
  }

  setUserRole(role: UserRole): void {
    const roleConfig = ROLE_CONFIGS[role] || ROLE_CONFIGS[UserRole.ADMIN];
    const resolvedRole = roleConfig.role;
    this.currentUserRole.set(resolvedRole);
    this.userPermissions.set(roleConfig.permissions);

    this.logger.info(
      'User role set',
      { role: resolvedRole, permissions: roleConfig.permissions },
      'RoleService'
    );
  }

  getCurrentUserRole(): UserRole | null {
    return this.currentUserRole();
  }

  getUserPermissions(): RolePermissions | null {
    return this.userPermissions();
  }

  getRoleConfig(role: UserRole): RoleConfig {
    return ROLE_CONFIGS[role];
  }

  getDefaultRoute(role: UserRole): string {
    return ROLE_CONFIGS[role]?.defaultRoute || ROLE_CONFIGS[UserRole.ADMIN].defaultRoute;
  }

  /**
   * Determine whether a given route is permitted for a specified user role.
   * @example
   * asRoutePermission('/campaign/123', UserRole.MANAGER)
   * true
   * @param {string} route - The route path to check (e.g., '/campaign/123').
   * @param {UserRole} role - The user role to evaluate permissions for.
   * @returns {boolean} Whether the role is allowed to access the route.
   **/
  hasRoutePermission(route: string, role: UserRole): boolean {
    const roleConfig = ROLE_CONFIGS[role];

    // First check for exact match
    if (roleConfig.allowedRoutes.includes(route)) {
      return true;
    }

    if (role === UserRole.SUPER_ADMIN && route.startsWith('/admin/')) {
      return true;
    }

    for (const baseRoute of ROUTES_ALLOWING_SUBPATHS) {
      if (route.startsWith(`${baseRoute}/`) && roleConfig.allowedRoutes.includes(baseRoute)) {
        return true;
      }
    }

    return false;
  }

  hasPermission(permission: keyof RolePermissions): boolean {
    const permissions = this.userPermissions();
    return permissions ? permissions[permission] : false;
  }

  isAdmin(): boolean {
    return this.currentUserRole() === UserRole.ADMIN;
  }

  /**
   * Resolve whether the current user is an admin, including a localStorage fallback
   * for cases where the role signal has not yet been initialized.
   */
  resolveIsAdmin(): boolean {
    let userRole = this.getCurrentUserRole();

    if (!userRole) {
      const userData = getActiveUserDataRaw();
      if (userData) {
        try {
          const parsed = JSON.parse(userData);
          userRole = parsed.role as UserRole;
        } catch {
          // ignore parse errors
        }
      }
    }

    return userRole === UserRole.ADMIN;
  }

  /**
   * Operational shop-floor role (`users.role = attendant`).
   * Attendant plus is a separate Permission — use isShopFloorEmployee() for shared shop-floor UI.
   */
  isAttendant(): boolean {
    return this.currentUserRole() === UserRole.ATTENDANT;
  }

  /** Shop-floor: attendant or attendant plus. */
  isShopFloorEmployee(): boolean {
    const role = this.currentUserRole();
    return role === UserRole.ATTENDANT || role === UserRole.ATTENDANT_PLUS;
  }

  /** Customers tab managers: admin and attendant plus. */
  canManageCustomers(): boolean {
    return this.hasPermission('canManageCustomers');
  }

  /**
   * Attendance-only shop employee (`users.role = staff`).
   */
  isStaff(): boolean {
    return this.currentUserRole() === UserRole.STAFF;
  }

  /** Non-admin shop employee (shop-floor or attendance-only). */
  isShopEmployee(): boolean {
    return this.isShopFloorEmployee() || this.isStaff();
  }

  isSuperAdmin(): boolean {
    return this.currentUserRole() === UserRole.SUPER_ADMIN;
  }

  getDisplayRole(): string {
    const role = this.currentUserRole();
    return role ? ROLE_CONFIGS[role].displayName : 'User';
  }

  /**
   * Clear user role and permissions (used on logout)
   */
  clearUserRole(): void {
    this.currentUserRole.set(null);
    this.userPermissions.set(null);
    this.logger.info('User role cleared', {}, 'RoleService');
  }
}
